/**
 * SupportChat - Chat di supporto real-time
 * Permette agli utenti di chattare con l'admin
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { MessageCircle, X, Send, Paperclip, Minimize2, Maximize2, Circle, Users, Camera } from 'lucide-react';
import { useAuth0UserId } from '../hooks/useAuth0UserId';
import { isAdminUser, getUserRole } from '../auth/auth0-config';
import * as chatApi from '../services/chatApi';
import html2canvas from 'html2canvas';

// Ruoli che possono usare la chat di supporto
const ALLOWED_CHAT_ROLES = ['business', 'enterprise', 'admin'];

interface Message {
  id: string;
  sender_id: string;
  sender_type: 'user' | 'admin';
  content: string;
  message_type: 'text' | 'image' | 'file';
  file_url?: string;
  file_name?: string;
  created_at: string;
  is_read: boolean;
}

interface Session {
  id: string;
  user_id: string;
  user_email?: string;
  status: string;
  created_at: string;
}

export default function SupportChat() {
  const { user, isAuthenticated } = useAuth0();
  const userId = useAuth0UserId();
  const isAdmin = isAdminUser(user);
  
  // Stati
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [adminOnline, setAdminOnline] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isTyping] = useState(false);  // Per future implementazioni typing indicator
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);
  
  // Admin: lista sessioni
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const presenceIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  
  // Carica sessione o crea nuova
  const loadOrCreateSession = useCallback(async () => {
    if (!userId || !user?.email) return;
    
    setIsLoading(true);
    try {
      const sess = await chatApi.getOrCreateSession(userId, user.email);
      setSession(sess);
      
      // Carica messaggi
      const msgs = await chatApi.getMessages(sess.id, userId);
      setMessages(msgs);
      
      // Marca come letti
      await chatApi.markAsRead(sess.id, userId);
      
    } catch (error) {
      console.error('Errore caricamento sessione:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId, user?.email]);
  
  // Admin: carica lista sessioni
  const loadSessions = useCallback(async () => {
    if (!userId || !user?.email || !isAdmin) return;
    
    try {
      const sess = await chatApi.listSessions(userId, user.email, 'open');
      setSessions(sess);
    } catch (error) {
      console.error('Errore caricamento sessioni:', error);
    }
  }, [userId, user?.email, isAdmin]);
  
  // Admin: seleziona sessione
  const selectSession = useCallback(async (sessId: string) => {
    if (!userId) return;
    
    setSelectedSessionId(sessId);
    setIsLoading(true);
    
    try {
      const msgs = await chatApi.getMessages(sessId, userId);
      setMessages(msgs);
      await chatApi.markAsRead(sessId, userId);
    } catch (error) {
      console.error('Errore caricamento messaggi:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);
  
  // Invia messaggio
  const sendMessage = useCallback(async () => {
    const currentSessionId = isAdmin ? selectedSessionId : session?.id;
    
    console.log('📤 [sendMessage] START', { 
      currentSessionId, 
      userId, 
      userEmail: user?.email,
      messageLength: newMessage.trim().length,
      isAdmin
    });
    
    if (!newMessage.trim()) {
      console.warn('⚠️ Messaggio vuoto');
      return;
    }
    
    if (!userId || !user?.email) {
      console.error('❌ Manca user ID o email');
      alert('❌ Errore: utente non identificato');
      return;
    }
    
    if (!currentSessionId) {
      console.error('❌ Manca session ID');
      alert('❌ Errore: sessione non trovata. Riapri la chat.');
      return;
    }
    
    setIsSending(true);
    
    try {
      console.log('🚀 Invio messaggio API...');
      const msg = await chatApi.sendMessage(
        currentSessionId,
        userId,
        user.email,
        newMessage.trim()
      );
      
      console.log('✅ Messaggio inviato:', msg);
      
      // Aggiungi messaggio localmente
      setMessages(prev => [...prev, msg]);
      setNewMessage('');
      scrollToBottom();
      
    } catch (error: any) {
      console.error('❌ Errore invio messaggio:', error);
      alert(`❌ Errore invio messaggio:\n${error?.message || error}`);
    } finally {
      setIsSending(false);
    }
  }, [newMessage, userId, user?.email, session?.id, selectedSessionId, isAdmin, scrollToBottom]);
  
  // Controlla admin online
  const checkAdminStatus = useCallback(async () => {
    try {
      const status = await chatApi.checkAdminOnline();
      setAdminOnline(status.online);
    } catch (error) {
      console.error('Errore check admin:', error);
    }
  }, []);
  
  // Aggiorna presenza (heartbeat)
  const updatePresence = useCallback(async () => {
    if (!userId || !user?.email) return;
    try {
      await chatApi.updatePresence(userId, user.email);
    } catch (error) {
      console.error('Errore update presenza:', error);
    }
  }, [userId, user?.email]);
  
  // Polling messaggi (fallback se WebSocket non funziona)
  const pollMessages = useCallback(async () => {
    const currentSessionId = isAdmin ? selectedSessionId : session?.id;
    if (!currentSessionId || !userId) return;
    
    try {
      const msgs = await chatApi.getMessages(currentSessionId, userId);
      setMessages(msgs);
    } catch (error) {
      console.error('Errore polling messaggi:', error);
    }
  }, [session?.id, selectedSessionId, userId, isAdmin]);
  
  // Conta non letti
  const updateUnreadCount = useCallback(async () => {
    if (!userId || !user?.email) return;
    try {
      const count = await chatApi.getUnreadCount(userId, user.email);
      setUnreadCount(count);
    } catch (error) {
      console.error('Errore count unread:', error);
    }
  }, [userId, user?.email]);
  
  // Cattura screenshot della schermata
  const captureScreenshot = useCallback(async () => {
    const currentSessionId = isAdmin ? selectedSessionId : session?.id;
    if (!userId || !user?.email || !currentSessionId) {
      alert('❌ Devi prima aprire una chat per inviare screenshot');
      return;
    }
    
    setIsCapturingScreenshot(true);
    
    try {
      // Nascondi temporaneamente la chat per lo screenshot
      const chatElement = document.querySelector('[data-support-chat]') as HTMLElement;
      if (chatElement) chatElement.style.display = 'none';
      
      // Cattura l'intera pagina (esclusa la chat)
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        scale: 0.5, // Riduci dimensioni per performance
      });
      
      // Rimostra la chat
      if (chatElement) chatElement.style.display = '';
      
      // Converti in base64
      const screenshotBase64 = canvas.toDataURL('image/png');
      
      // Invia come messaggio immagine
      const msg = await chatApi.sendMessage(
        currentSessionId,
        userId,
        user.email,
        '📸 Screenshot della schermata',
        'image',
        screenshotBase64,
        `screenshot_${new Date().toISOString().slice(0,19).replace(/:/g,'-')}.png`
      );
      
      // Aggiungi messaggio localmente
      setMessages(prev => [...prev, msg]);
      scrollToBottom();
      
      console.log('📸 Screenshot inviato con successo!');
      
    } catch (error) {
      console.error('❌ Errore cattura screenshot:', error);
      alert('❌ Errore durante la cattura dello screenshot');
    } finally {
      setIsCapturingScreenshot(false);
    }
  }, [userId, user?.email, session?.id, selectedSessionId, isAdmin, scrollToBottom]);
  
  // Effect: apertura chat
  useEffect(() => {
    if (isOpen && isAuthenticated && userId) {
      if (isAdmin) {
        loadSessions();
      } else {
        loadOrCreateSession();
      }
      checkAdminStatus();
      updatePresence();
      
      // Avvia polling messaggi ogni 3 secondi
      pollIntervalRef.current = setInterval(pollMessages, 3000);
      
      // Heartbeat presenza ogni 30 secondi
      presenceIntervalRef.current = setInterval(updatePresence, 30000);
    }
    
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (presenceIntervalRef.current) clearInterval(presenceIntervalRef.current);
    };
  }, [isOpen, isAuthenticated, userId, isAdmin, loadOrCreateSession, loadSessions, checkAdminStatus, updatePresence, pollMessages]);
  
  // Effect: check admin status periodicamente
  useEffect(() => {
    if (!isAdmin) {
      const interval = setInterval(checkAdminStatus, 60000); // ogni minuto
      return () => clearInterval(interval);
    }
  }, [isAdmin, checkAdminStatus]);
  
  // Effect: conta non letti
  useEffect(() => {
    if (isAuthenticated && userId) {
      updateUnreadCount();
      const interval = setInterval(updateUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, userId, updateUnreadCount]);
  
  // Effect: scroll quando arrivano messaggi
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);
  
  // Verifica ruolo utente
  const userRole = getUserRole(user);
  const canUseChat = ALLOWED_CHAT_ROLES.includes(userRole) || isAdmin;
  
  // Debug: log stato autenticazione
  console.log('💬 SupportChat:', { isAuthenticated, userId, userEmail: user?.email, userRole, canUseChat });
  
  // Non mostrare se non autenticato
  if (!isAuthenticated) {
    console.log('💬 SupportChat: non autenticato, nascondo bottone');
    return null;
  }
  
  // Non mostrare se ruolo non autorizzato (solo business, enterprise, admin)
  if (!canUseChat) {
    console.log('💬 SupportChat: ruolo non autorizzato:', userRole);
    return null;
  }
  
  // Bottone chat (floating)
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-6 p-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full shadow-2xl hover:shadow-3xl hover:scale-110 transition-all duration-300 z-40 group"
        title="Apri Chat Supporto"
      >
        <MessageCircle size={28} className="group-hover:animate-bounce" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        {/* Indicatore admin online */}
        {!isAdmin && (
          <span className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-white ${adminOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
        )}
      </button>
    );
  }
  
  // Finestra chat
  return (
    <div 
      data-support-chat="true"
      className={`fixed ${isMinimized ? 'bottom-20 right-6 w-80' : 'bottom-20 right-6 w-96 h-[550px]'} bg-white rounded-2xl shadow-2xl border-2 border-purple-200 z-40 flex flex-col overflow-hidden transition-all duration-300`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MessageCircle size={24} />
          <div>
            <h3 className="font-bold">{isAdmin ? '👑 Admin Chat' : 'Supporto'}</h3>
            <div className="text-xs flex items-center gap-1">
              {isAdmin ? (
                <span className="flex items-center gap-1">
                  <Users size={12} />
                  {sessions.length} richieste
                </span>
              ) : (
                <>
                  <Circle size={8} className={`fill-current ${adminOnline ? 'text-green-400' : 'text-gray-400'}`} />
                  {adminOnline ? 'Admin online' : 'Admin offline'}
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1.5 hover:bg-white/20 rounded-lg transition"
            title={isMinimized ? "Espandi" : "Minimizza"}
          >
            {isMinimized ? <Maximize2 size={18} /> : <Minimize2 size={18} />}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 hover:bg-white/20 rounded-lg transition"
            title="Chiudi"
          >
            <X size={18} />
          </button>
        </div>
      </div>
      
      {!isMinimized && (
        <>
          {/* Admin: Lista sessioni */}
          {isAdmin && !selectedSessionId && (
            <div className="flex-1 overflow-y-auto p-4">
              <h4 className="text-sm font-bold text-gray-700 mb-3">📬 Richieste di supporto</h4>
              {sessions.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Nessuna richiesta</p>
              ) : (
                <div className="space-y-2">
                  {sessions.map(sess => (
                    <button
                      key={sess.id}
                      onClick={() => selectSession(sess.id)}
                      className="w-full p-3 bg-gray-100 hover:bg-purple-100 rounded-xl text-left transition"
                    >
                      <div className="font-semibold text-gray-800">{sess.user_email || 'Utente'}</div>
                      <div className="text-xs text-gray-500">
                        {new Date(sess.created_at).toLocaleString('it-IT')}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Chat messages */}
          {(!isAdmin || selectedSessionId) && (
            <>
              {isAdmin && selectedSessionId && (
                <button
                  onClick={() => { setSelectedSessionId(null); setMessages([]); }}
                  className="px-4 py-2 bg-gray-100 text-sm text-purple-600 hover:bg-gray-200 transition"
                >
                  ← Torna alla lista
                </button>
              )}
              
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <MessageCircle size={40} className="mx-auto mb-2 opacity-30" />
                    <p>Inizia la conversazione!</p>
                  </div>
                ) : (
                  messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_type === (isAdmin ? 'admin' : 'user') ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-2xl ${
                          msg.sender_type === (isAdmin ? 'admin' : 'user')
                            ? 'bg-purple-600 text-white rounded-br-md'
                            : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md'
                        }`}
                      >
                        {msg.message_type === 'image' && msg.file_url && (
                          <img src={msg.file_url} alt="Immagine" className="max-w-full rounded-lg mb-2" />
                        )}
                        {msg.message_type === 'file' && msg.file_url && (
                          <a 
                            href={msg.file_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-blue-500 underline"
                          >
                            <Paperclip size={14} />
                            {msg.file_name || 'File'}
                          </a>
                        )}
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        <span className={`text-xs mt-1 block ${
                          msg.sender_type === (isAdmin ? 'admin' : 'user') ? 'text-purple-200' : 'text-gray-400'
                        }`}>
                          {new Date(msg.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  ))
                )}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-gray-200 text-gray-600 px-4 py-2 rounded-full text-sm">
                      <span className="animate-pulse">Sta scrivendo...</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
              
              {/* Input */}
              <div className="p-3 border-t border-gray-200 bg-white">
                <div className="flex items-center gap-2">
                  {/* Bottone Screenshot */}
                  <button
                    onClick={captureScreenshot}
                    disabled={isCapturingScreenshot || isSending}
                    className="p-2.5 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 text-white rounded-full transition"
                    title="📸 Cattura screenshot della schermata"
                  >
                    {isCapturingScreenshot ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    ) : (
                      <Camera size={20} />
                    )}
                  </button>
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    placeholder="Scrivi un messaggio..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                    disabled={isSending}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || isSending}
                    className="p-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white rounded-full transition"
                    title="Invia"
                  >
                    {isSending ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    ) : (
                      <Send size={20} />
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

